import React, { useState, useEffect } from 'react';
import {
  Card, Tabs, Table, Button, Upload, Modal, Form, Input, Select,
  Tag, Space, Popconfirm, Row, Col, Statistic, Typography,
  Tooltip, Badge, Alert, Divider, Spin, DatePicker, App
} from 'antd';
import {
  FolderOutlined, UploadOutlined, DownloadOutlined, DeleteOutlined,
  FileTextOutlined, FilePdfOutlined, FileWordOutlined, FileOutlined,
  FilterOutlined, RobotOutlined, ThunderboltOutlined, BarChartOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuthStore } from '../store/useAuthStore';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Documents = () => {
  const { message } = App.useApp();
  const { user } = useAuthStore();
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [projects, setProjects] = useState([]);
  const [documentStats, setDocumentStats] = useState(null);
  const [templateStats, setTemplateStats] = useState(null);
  const [uploadTemplateModalVisible, setUploadTemplateModalVisible] = useState(false);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [templateForm] = Form.useForm();
  const [generateForm] = Form.useForm();
  const [filterProject, setFilterProject] = useState(null);
  const [filterType, setFilterType] = useState(null);

  const documentTypes = ['NDA', 'SOW', 'MSA', 'Contract', 'Agreement', 'Other'];

  useEffect(() => {
    fetchProjects();
    if (activeTab === 'overview') fetchOverviewData();
    else if (activeTab === 'templates') fetchTemplates();
    else if (activeTab === 'documents') fetchDocuments();
  }, [activeTab, filterProject, filterType]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${apiUrl}/api/projects`);
      const data = await response.json();
      if (data.success) setProjects(data.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchOverviewData = async () => {
    try {
      setLoading(true);
      const [docsRes, templatesRes] = await Promise.all([
        fetch(`${apiUrl}/api/documents/stats/summary`),
        fetch(`${apiUrl}/api/templates/stats/summary`)
      ]);
      const docsData = await docsRes.json();
      const templatesData = await templatesRes.json();
      if (docsData.success) setDocumentStats(docsData.data);
      if (templatesData.success) setTemplateStats(templatesData.data);
    } catch (error) {
      console.error('Error fetching overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      let url = `${apiUrl}/api/templates?status=active`;
      if (filterType) url += `&type=${filterType}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) setTemplates(data.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
      message.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      let url = `${apiUrl}/api/documents?status=active`;
      if (filterProject) url += `&projectId=${filterProject}`;
      if (filterType) url += `&type=${filterType}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) setDocuments(data.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
      message.error('Failed to fetch documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadTemplate = async (values) => {
    try {
      if (!user || !user._id) {
        message.error('User not logged in. Please login again.');
        return;
      }

      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('type', values.type);
      formData.append('createdBy', user._id);
      if (values.description) formData.append('description', values.description);
      if (values.headerContent) formData.append('headerContent', values.headerContent);
      if (values.footerContent) formData.append('footerContent', values.footerContent);
      if (values.aiPromptTemplate) formData.append('aiPromptTemplate', values.aiPromptTemplate);
      
      if (values.companyName) {
        const coverPageData = {
          companyName: values.companyName,
          companyAddress: values.companyAddress,
          companyPhone: values.companyPhone,
          companyEmail: values.companyEmail,
          companyWebsite: values.companyWebsite
        };
        formData.append('coverPageData', JSON.stringify(coverPageData));
      }
      
      if (values.template?.[0]) formData.append('template', values.template[0].originFileObj);
      if (values.logo?.[0]) formData.append('logo', values.logo[0].originFileObj);

      const response = await fetch(`${apiUrl}/api/templates/upload`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();

      if (data.success) {
        message.success('Template uploaded successfully');
        setUploadTemplateModalVisible(false);
        templateForm.resetFields();
        fetchTemplates();
      } else {
        message.error(data.message || 'Failed to upload template');
      }
    } catch (error) {
      console.error('Error uploading template:', error);
      message.error('Failed to upload template');
    }
  };

  const handleGenerateDocument = async (values) => {
    try {
      if (!user || !user._id) {
        message.error('User not logged in. Please login again.');
        return;
      }

      setGenerating(true);
      const response = await fetch(`${apiUrl}/api/templates/${selectedTemplate._id}/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: values.projectId,
          uploadedBy: user._id,
          customData: values
        })
      });
      const data = await response.json();

      if (data.success) {
        message.success('Document generated successfully!');
        setShowGenerateForm(false);
        generateForm.resetFields();
        setSelectedTemplate(null);
        fetchDocuments();
      } else {
        message.error(data.message || 'Failed to generate document');
      }
    } catch (error) {
      console.error('Error generating document:', error);
      message.error('Failed to generate document');
    } finally {
      setGenerating(false);
    }
  };

  const handleDeleteTemplate = async (id) => {
    try {
      const response = await fetch(`${apiUrl}/api/templates/${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        message.success('Template deleted successfully');
        fetchTemplates();
      } else {
        message.error(data.message || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      message.error('Failed to delete template');
    }
  };

  const handleDeleteDocument = async (id) => {
    try {
      const response = await fetch(`${apiUrl}/api/documents/${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        message.success('Document deleted successfully');
        fetchDocuments();
      } else {
        message.error(data.message || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      message.error('Failed to delete document');
    }
  };

  const handleDownload = (downloadUrl, fileName) => {
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = fileName;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getFileIcon = (fileType) => {
    if (fileType?.includes('pdf')) return <FilePdfOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />;
    if (fileType?.includes('word')) return <FileWordOutlined style={{ color: '#1890ff', fontSize: '20px' }} />;
    return <FileOutlined style={{ color: '#8c8c8c', fontSize: '20px' }} />;
  };

  const getTypeColor = (type) => {
    const colors = { 'NDA': 'red', 'SOW': 'blue', 'MSA': 'purple', 'Contract': 'orange', 'Agreement': 'cyan', 'Other': 'default' };
    return colors[type] || 'default';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const renderOverviewTab = () => (
    <div>
      <Row gutter={[16, 16]} className="mb-6">
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Total Documents" value={documentStats?.total || 0} prefix={<FileTextOutlined />} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Total Templates" value={templateStats?.total || 0} prefix={<FolderOutlined />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Documents Generated" value={templateStats?.totalUsage || 0} prefix={<RobotOutlined />} valueStyle={{ color: '#722ed1' }} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Total Storage" value={formatFileSize(documentStats?.totalSize || 0)} prefix={<BarChartOutlined />} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="🤖 AI Document Generation" bordered={false}>
            <Alert message="Intelligent Document Creation" description="Upload templates with your branding. AI generates professional legal documents for each project." type="info" showIcon className="mb-4" />
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button type="primary" icon={<UploadOutlined />} size="large" block onClick={() => { setActiveTab('templates'); setTimeout(() => setUploadTemplateModalVisible(true), 100); }}>Upload New Template</Button>
              <Button icon={<ThunderboltOutlined />} size="large" block onClick={() => setActiveTab('templates')}>Generate Document</Button>
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="📊 Document Types" bordered={false}>
            {documentStats?.byType?.length > 0 ? (
              <Space direction="vertical" style={{ width: '100%' }}>
                {documentStats.byType.map((item, idx) => (
                  <div key={idx} style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Tag color={getTypeColor(item._id)}>{item._id}</Tag>
                    <Text strong>{item.count} documents</Text>
                  </div>
                ))}
              </Space>
            ) : <Text type="secondary">No documents yet</Text>}
          </Card>
        </Col>
      </Row>
    </div>
  );

  const renderTemplatesTab = () => {
    const templateColumns = [
      { title: 'Template Name', dataIndex: 'name', key: 'name', render: (text, record) => (
        <Space>{getFileIcon(record.templateFileName)}<div><Text strong>{text}</Text><br/><Text type="secondary" style={{ fontSize: '12px' }}>{record.description}</Text></div></Space>
      )},
      { title: 'Type', dataIndex: 'type', key: 'type', render: (type) => <Tag color={getTypeColor(type)}>{type}</Tag> },
      { title: 'Usage', dataIndex: 'usageCount', key: 'usageCount', render: (count) => <Badge count={count} showZero style={{ backgroundColor: '#52c41a' }} /> },
      { title: 'Last Used', dataIndex: 'lastUsed', key: 'lastUsed', render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : <Text type="secondary">Never</Text> },
      { title: 'Actions', key: 'actions', fixed: 'right', width: 180, render: (_, record) => (
        <Space>
          <Tooltip title="Generate Document"><Button type="primary" icon={<ThunderboltOutlined />} onClick={() => { setSelectedTemplate(record); generateForm.resetFields(); setActiveTab('documents'); setTimeout(() => setShowGenerateForm(true), 100); }}>Generate</Button></Tooltip>
          <Tooltip title="Download"><Button type="text" icon={<DownloadOutlined />} onClick={() => handleDownload(record.downloadUrl, record.templateFileName)} /></Tooltip>
          <Popconfirm title="Delete Template?" onConfirm={() => handleDeleteTemplate(record._id)}><Tooltip title="Delete"><Button type="text" danger icon={<DeleteOutlined />} /></Tooltip></Popconfirm>
        </Space>
      )}
    ];

    return (
      <div>
        <Card className="mb-4">
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={8}>
              <Select placeholder="Filter by Type" value={filterType} onChange={setFilterType} allowClear style={{ width: '100%' }}>
                {documentTypes.map(type => <Option key={type} value={type}>{type}</Option>)}
              </Select>
            </Col>
            <Col xs={24} md={8}>
              <Button icon={<FilterOutlined />} onClick={() => setFilterType(null)}>Clear Filters</Button>
            </Col>
            <Col xs={24} md={8} style={{ textAlign: 'right' }}>
              <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadTemplateModalVisible(true)}>Upload Template</Button>
            </Col>
          </Row>
        </Card>
        <Card>
          <Table columns={templateColumns} dataSource={templates} rowKey="_id" loading={loading} scroll={{ x: 1000 }} pagination={{ defaultPageSize: 10, showSizeChanger: true, showTotal: (total) => `Total ${total} templates` }} />
        </Card>
      </div>
    );
  };

  const renderDocumentsTab = () => {
    const documentColumns = [
      { title: 'File', dataIndex: 'fileName', key: 'fileName', render: (text, record) => (
        <Space>{getFileIcon(record.fileType)}<div><Text strong>{record.name}</Text><br/><Text type="secondary" style={{ fontSize: '12px' }}>{text}</Text></div></Space>
      ), width: 200 },
      { title: 'Type', dataIndex: 'type', key: 'type', render: (type) => <Tag color={getTypeColor(type)}>{type}</Tag>, width: 100 },
      { title: 'Project', dataIndex: 'projectId', key: 'project', render: (project) => (
        <div><Text strong>{project?.name}</Text><br/><Text type="secondary" style={{ fontSize: '12px' }}>{project?.client}</Text></div>
      ), width: 150 },
      { title: 'Size', dataIndex: 'formattedSize', key: 'size', width: 80 },
      { title: 'Date', dataIndex: 'createdAt', key: 'createdAt', render: (date) => dayjs(date).format('MMM DD, YY'), width: 100 },
      { title: 'Actions', key: 'actions', fixed: 'right', width: 80, render: (_, record) => (
        <Space>
          <Tooltip title="Download"><Button type="text" icon={<DownloadOutlined />} onClick={() => handleDownload(record.downloadUrl, record.fileName)} /></Tooltip>
          <Popconfirm title="Delete?" onConfirm={() => handleDeleteDocument(record._id)}><Tooltip title="Delete"><Button type="text" danger icon={<DeleteOutlined />} /></Tooltip></Popconfirm>
        </Space>
      )}
    ];

    return (
      <Row gutter={[24, 24]}>
        {/* Main Content - 70% */}
        <Col xs={24} xl={17}>
          <Card className="mb-4">
            <Row gutter={[16, 16]} align="middle">
              <Col xs={24} md={10}>
                <Input.Search
                  placeholder="Search documents..."
                  allowClear
                  onSearch={(value) => console.log('Search:', value)}
                  style={{ width: '100%' }}
                />
              </Col>
              <Col xs={24} md={6}>
                <Select placeholder="Filter by Project" value={filterProject} onChange={setFilterProject} allowClear style={{ width: '100%' }}>
                  {projects.map(project => <Option key={project._id} value={project._id}>{project.name}</Option>)}
                </Select>
              </Col>
              <Col xs={24} md={5}>
                <Select placeholder="Filter by Type" value={filterType} onChange={setFilterType} allowClear style={{ width: '100%' }}>
                  {documentTypes.map(type => <Option key={type} value={type}>{type}</Option>)}
                </Select>
              </Col>
              <Col xs={24} md={3}>
                <Button icon={<FilterOutlined />} onClick={() => { setFilterProject(null); setFilterType(null); }}>Clear</Button>
              </Col>
            </Row>
          </Card>
          <Card>
            <Table 
              columns={documentColumns} 
              dataSource={documents} 
              rowKey="_id" 
              loading={loading} 
              scroll={{ x: 800 }} 
              pagination={{ 
                defaultPageSize: 10, 
                showSizeChanger: true, 
                showTotal: (total) => `Total ${total} documents` 
              }} 
            />
          </Card>
        </Col>

        {/* AI Document Generator - 30% */}
        <Col xs={24} xl={7}>
          {!showGenerateForm ? (
            <Card 
              title={
                <Space>
                  <RobotOutlined style={{ color: '#1890ff' }} />
                  <Text strong>AI Document Generator</Text>
                </Space>
              }
              style={{ position: 'sticky', top: '20px' }}
            >
              <Alert
                message="Generate Legal Documents"
                description="Create professional documents with AI assistance"
                type="info"
                showIcon
                className="mb-4"
              />
              
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                size="large"
                block
                onClick={() => setShowGenerateForm(true)}
                style={{ marginBottom: '16px' }}
              >
                Generate New Document
              </Button>

              <Divider>Quick Stats</Divider>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">Total Documents:</Text>
                  <Text strong>{documents.length}</Text>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Text type="secondary">This Month:</Text>
                  <Text strong>{documents.filter(d => dayjs(d.createdAt).isAfter(dayjs().subtract(30, 'days'))).length}</Text>
                </div>
              </Space>

              <Divider>Document Types</Divider>
              <Space direction="vertical" style={{ width: '100%' }}>
                {documentTypes.map(type => {
                  const count = documents.filter(d => d.type === type).length;
                  return count > 0 ? (
                    <div key={type} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Tag color={getTypeColor(type)}>{type}</Tag>
                      <Text>{count}</Text>
                    </div>
                  ) : null;
                })}
              </Space>
            </Card>
          ) : (
            <Card 
              title={
                <Space>
                  <RobotOutlined style={{ color: '#1890ff' }} />
                  <Text strong>Generate Document with AI</Text>
                </Space>
              }
              extra={
                <Button 
                  type="text" 
                  onClick={() => {
                    setShowGenerateForm(false);
                    generateForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
              }
              style={{ position: 'sticky', top: '20px', maxHeight: '90vh', overflowY: 'auto' }}
            >
              <Spin spinning={generating} tip="AI is generating your document...">
                <Alert 
                  message="AI-Powered Generation" 
                  description="Fill in the details below to generate a professional legal document." 
                  type="info" 
                  showIcon 
                  className="mb-4" 
                />
                
                <Form form={generateForm} layout="vertical" onFinish={handleGenerateDocument}>
                  <Form.Item name="projectId" label="Project" rules={[{ required: true, message: 'Required' }]}>
                    <Select placeholder="Select project" showSearch optionFilterProp="children">
                      {projects.map(project => <Option key={project._id} value={project._id}>{project.name} - {project.client}</Option>)}
                    </Select>
                  </Form.Item>

                  <Form.Item name="documentType" label="Document Type" rules={[{ required: true, message: 'Required' }]}>
                    <Select placeholder="Select type">
                      {documentTypes.map(type => <Option key={type} value={type}>{type}</Option>)}
                    </Select>
                  </Form.Item>

                  <Divider orientation="left" style={{ fontSize: '14px' }}>Qodequay Details</Divider>

                  <Form.Item name={['party1', 'name']} label="Organization" initialValue="One Qodequay">
                    <Input placeholder="Organization name" />
                  </Form.Item>

                  <Form.Item name={['party1', 'registrationNumber']} label="Reg. Number">
                    <Input placeholder="Registration number" />
                  </Form.Item>

                  <Form.Item name={['party1', 'address']} label="Address">
                    <TextArea rows={2} placeholder="Registered address" />
                  </Form.Item>

                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item name={['party1', 'taxId']} label="GST/Tax ID">
                        <Input placeholder="Tax ID" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['party1', 'pan']} label="PAN">
                        <Input placeholder="PAN" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item name={['party1', 'signingAuthority']} label="Signatory" initialValue="Authorized Signatory">
                        <Input placeholder="Name" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['party1', 'designation']} label="Designation">
                        <Input placeholder="Title" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider orientation="left" style={{ fontSize: '14px' }}>Client/Partner Details</Divider>

                  <Form.Item name={['party2', 'name']} label="Organization" rules={[{ required: true, message: 'Required' }]}>
                    <Input placeholder="Client organization" />
                  </Form.Item>

                  <Form.Item name={['party2', 'registrationNumber']} label="Reg. Number">
                    <Input placeholder="Registration number" />
                  </Form.Item>

                  <Form.Item name={['party2', 'address']} label="Address" rules={[{ required: true, message: 'Required' }]}>
                    <TextArea rows={2} placeholder="Registered address" />
                  </Form.Item>

                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item name={['party2', 'taxId']} label="GST/Tax ID">
                        <Input placeholder="Tax ID" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['party2', 'pan']} label="PAN">
                        <Input placeholder="PAN" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={8}>
                    <Col span={12}>
                      <Form.Item name={['party2', 'signingAuthority']} label="Signatory" rules={[{ required: true, message: 'Required' }]}>
                        <Input placeholder="Name" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item name={['party2', 'designation']} label="Designation">
                        <Input placeholder="Title" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider orientation="left" style={{ fontSize: '14px' }}>Additional Details</Divider>

                  <Form.Item name="effectiveDate" label="Effective Date">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>

                  <Form.Item name="additionalInfo" label="Additional Info">
                    <TextArea rows={3} placeholder="Specific clauses or terms..." />
                  </Form.Item>

                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    icon={<ThunderboltOutlined />} 
                    loading={generating}
                    block
                    size="large"
                  >
                    Generate with AI
                  </Button>
                </Form>
              </Spin>
            </Card>
          )}
        </Col>
      </Row>
    );
  };

  const tabItems = [
    { key: 'overview', label: <span><BarChartOutlined />Overview</span>, children: renderOverviewTab() },
    { key: 'templates', label: <span><FolderOutlined />Templates</span>, children: renderTemplatesTab() },
    { key: 'documents', label: <span><FileTextOutlined />Documents</span>, children: renderDocumentsTab() }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <Title level={2}><RobotOutlined className="mr-2 text-blue-600" />AI Document Management</Title>
      </div>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} size="large" />

      <Modal title="Upload Document Template" open={uploadTemplateModalVisible} onCancel={() => { setUploadTemplateModalVisible(false); templateForm.resetFields(); }} onOk={() => templateForm.submit()} width={700}>
        <Form form={templateForm} layout="vertical" onFinish={handleUploadTemplate}>
          <Form.Item name="name" label="Template Name" rules={[{ required: true }]}><Input placeholder="e.g., Standard NDA Template" /></Form.Item>
          <Form.Item name="type" label="Document Type" rules={[{ required: true }]}><Select placeholder="Select type">{documentTypes.map(type => <Option key={type} value={type}>{type}</Option>)}</Select></Form.Item>
          <Form.Item name="description" label="Description"><TextArea rows={2} placeholder="Brief description" /></Form.Item>
          <Divider>Branding</Divider>
          <Row gutter={16}>
            <Col span={12}><Form.Item name="companyName" label="Company Name"><Input /></Form.Item></Col>
            <Col span={12}><Form.Item name="companyEmail" label="Email"><Input /></Form.Item></Col>
          </Row>
          <Form.Item name="companyAddress" label="Address"><TextArea rows={2} /></Form.Item>
          <Form.Item name="headerContent" label="Header (Optional)"><Input /></Form.Item>
          <Form.Item name="footerContent" label="Footer (Optional)"><Input /></Form.Item>
          <Form.Item name="aiPromptTemplate" label="AI Instructions (Optional)"><TextArea rows={3} placeholder="Specific instructions for AI..." /></Form.Item>
          <Divider>Files</Divider>
          <Form.Item name="template" label="Template File" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList} rules={[{ required: true }]}>
            <Upload beforeUpload={() => false} maxCount={1} accept=".docx,.pdf,.doc"><Button icon={<UploadOutlined />}>Select File</Button></Upload>
          </Form.Item>
          <Form.Item name="logo" label="Logo (Optional)" valuePropName="fileList" getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}>
            <Upload beforeUpload={() => false} maxCount={1} accept="image/*" listType="picture"><Button icon={<UploadOutlined />}>Select Logo</Button></Upload>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Documents;
